'use client'

import React, { createContext, useContext, useCallback, useMemo, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { AccessDeniedModal } from '@/components/access/AccessDeniedModal'
import type { AccessResult } from '@/components/access/types'

type GateOptions = {
  title?: string
}

type FunnelGateCtx = {
  guard: (fn: () => void | Promise<void>, opts?: GateOptions) => Promise<void>
  check: (opts?: GateOptions) => Promise<boolean>
}

const Ctx = createContext<FunnelGateCtx | null>(null)

export function FunnelGateProvider({
  sellerId,
  isOwner = false,
  children,
}: {
  sellerId?: number | null
  isOwner?: boolean
  children: React.ReactNode
}) {
  const [access, setAccess] = useState<AccessResult | null>(null)
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('Desbloquea el Constructor de Embudos')

  const check = useCallback(
    async (opts?: GateOptions) => {
      if (opts?.title) setTitle(opts.title)

      if (isOwner) return true

      // si no hay sellerId, tratamos como denegado (no sabemos qué producto FUNNELS corresponde)
      if (!sellerId) {
        setAccess({ allowed: false, reason: 'product', productIds: [] } as any)
        setOpen(true)
        return false
      }

      const res = await fetch(`/api/access/funnels?seller_id=${sellerId}`, { cache: 'no-store' })
      const data = (await res.json()) as AccessResult

      if (data.allowed) return true

      setAccess(data)
      setOpen(true)
      return false
    },
    [sellerId, isOwner]
  )

  const guard = useCallback(
    async (fn: () => void | Promise<void>, opts?: GateOptions) => {
      const ok = await check(opts)
      if (!ok) return
      await fn()
    },
    [check]
  )

  const value = useMemo(() => ({ guard, check }), [guard, check])

  return (
    <Ctx.Provider value={value}>
      {children}

      {/* Modal global para TODO Funnels */}
      {access && !access.allowed && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md">
            <AccessDeniedModal
              title={title}
              access={access}
              type="tool"
              mode="restricted"
              step="preview"
            />
          </DialogContent>
        </Dialog>
      )}
    </Ctx.Provider>
  )
}

export function useFunnelGate() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useFunnelGate must be used within <FunnelGateProvider />')
  return v
}
